import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  EventTypeCategory
} from '../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { ListResponse } from '../../../../../../models/list-response';
import { dateStringFromISO8601 } from '../../../../../../utils';
import { AccountService } from '../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-event-type-categories',
  templateUrl: './event-type-categories.component.html',
  styleUrls: ['./event-type-categories.component.css']
})
export class EventTypeCategoriesComponent implements OnInit {
  public resultList: ListResponse<EventTypeCategory>;

  // Soft-deleted categories, shown in a separate "Removed" section with a
  // Restore action (PRO-1310).
  public removedCategories: EventTypeCategory[] = [];
  public restoringCategoryId: number = null;

  public categoriesReorderedSubject = new Subject<null>();
  public reorderRequestCount = 0;

  public loadingResults = false;
  public loadingReorder = false;

  public serverError: number = null;
  public errorMessage = '';
  public successMessage = '';

  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private activatedRoute: ActivatedRoute,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_events) {
      return;
    }

    // Surface any post-navigation feedback (e.g. from creating or removing a
    // category on the detail page) in the same dismissable floating alert as
    // the reorder/restore messages, rather than a separate fixed banner.
    const feedback = history.state && history.state.feedback;
    if (feedback) {
      if (feedback.status === 'success') {
        this.successMessage = feedback.text;
      } else {
        this.errorMessage = feedback.text;
      }
    }

    this.loadCategories();
    this.loadRemovedCategories();

    this.categoriesReorderedSubject.pipe(
      // Avoid rapid requests by waiting 1s before applying the order.
      debounceTime(1000)
    ).subscribe(() => {
      this.updateCategoryOrderNumbers();
    });
  }

  public loadCategories() {
    this.loadingResults = true;

    this.eventTypeService
      .getEventTypeCategories(1, 100)
      .then(response => {
        this.resultList = response;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingResults = false;
      });
  }

  /**
   * Load the org's removed (soft-deleted) categories for the "Removed"
   * section. Uses the backend's maximum page size — this section is the only
   * restore surface, so no removed category may be silently truncated away.
   */
  public loadRemovedCategories() {
    this.eventTypeService
      .getEventTypeCategories(1, 3000, 'removed')
      .then(response => {
        this.removedCategories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      });
  }

  /**
   * Restore a removed category, moving it back into the active list.
   */
  public restoreCategory(category: EventTypeCategory) {
    this.errorMessage = '';
    this.successMessage = '';
    this.restoringCategoryId = category.id;

    this.eventTypeService
      .restoreEventTypeCategory(category.id)
      .then(() => {
        this.successMessage = `Category "${category.name}" restored.`;
        this.loadCategories();
        this.loadRemovedCategories();
      })
      .catch(reason => {
        if (reason.status === 400 && reason.error && reason.error.useful_error_message) {
          // e.g. the category's URL hash was reused while it was removed.
          this.errorMessage = reason.error.useful_error_message;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to restore this category.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.restoringCategoryId = null;
      });
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.resultList &&
      this.resultList.results &&
      (this.resultList.results.length > 0)
    );
  }

  /**
   * Move the given category up or down in the list.
   */
  public shiftCategory(category: EventTypeCategory, shiftUp: boolean, toEnd: boolean) {
    const idx = this.resultList.results.indexOf(category);

    if (toEnd) {
      if (shiftUp) {
        this.resultList.results.splice(idx, 1);
        this.resultList.results.unshift(category);
      } else {
        this.resultList.results.splice(idx, 1);
        this.resultList.results.push(category);
      }
    } else {
      if (shiftUp) {
        this.resultList.results[idx] = this.resultList.results[idx - 1];
        this.resultList.results[idx - 1] = category;
      } else {
        this.resultList.results[idx] = this.resultList.results[idx + 1];
        this.resultList.results[idx + 1] = category;
      }
    }

    this.loadingReorder = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.categoriesReorderedSubject.next();
  }

  public updateCategoryOrderNumbers() {
    this.loadingReorder = true;

    this.errorMessage = '';
    this.successMessage = '';

    this.reorderRequestCount++;
    const thisRequestCount = this.reorderRequestCount;

    let orderNum = 1;
    for (const ec of this.resultList.results) {
      ec.order_number = orderNum + 1;
      orderNum += 1;
    }

    this.eventTypeService
      .setEventTypeCategoriesOrderNumbers(this.resultList.results)
      .then(() => {
        this.successMessage = 'Categories reordered successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to reorder these categories.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.reorderRequestCount) {
          this.loadingReorder = false;
        }
      });
  }
}
